<script lang="ts" setup>
import { ref, computed } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import PanelGrid from "@/views/shared/components/PanelGrid.vue";
import PanelCard from "@/views/shared/components/PanelCard.vue";
import PageHeader from "@/components/PageHeader.vue";
import DataView from "@/components/DataView.vue";
import {
  useAllActivities,
  renderActivityTitle,
  activityIconClass,
  activityColorClass,
  activitySeverityBadgeClass,
  formatActivityTimestamp,
  ACTIVITY_CATEGORY_OPTIONS,
  ACTIVITY_SEVERITY_OPTIONS,
} from "@/queries/activities";
import type {
  AllActivitiesRow,
  ActivityCategory,
  ActivitySeverity,
} from "@/types";
import type { Config } from "datatables.net";

// All orgs in the system — fetched separately from the activities feed so the org dropdown
// doesn't collapse to just the currently-filtered org when filtering is applied.
interface OrgListItem { orgId: string; orgName: string }
const { data: allOrgsData } = useQuery({
  queryKey: ["admin-activity-orgs"],
  queryFn: () => fetchWrapper.get<OrgListItem[]>(`${import.meta.env.VITE_API_URL}/orgs`),
});

// =====================================================================
// Filter state — server-side filters drive the live query reactively;
// the search box narrows results client-side over the loaded page.
// =====================================================================
const searchQuery = ref("");
const selectedOrgId = ref<string>("");
const selectedCategory = ref<ActivityCategory | "">("");
const selectedSeverity = ref<ActivitySeverity | "">("");
const selectedActorUserId = ref<string>("");
const dateFrom = ref("");
const dateTo = ref("");

const serverOrgId = computed(() => selectedOrgId.value || undefined);

// Users for the currently-filtered org — cascading dropdown. Only fetched when an org is
// selected; otherwise the Actor dropdown falls back to deriving names from loaded activities.
// SuperAdmin passes the CanManageUsers gate on /api/orgs/{orgId}/users so this is reachable
// for any org id without a separate admin-only endpoint.
interface OrgUserListItem { id: string; firstName?: string; lastName?: string; email?: string }
const { data: orgUsersData } = useQuery({
  queryKey: ["admin-activity-org-users", serverOrgId],
  queryFn: () =>
    fetchWrapper.get<OrgUserListItem[]>(`${import.meta.env.VITE_API_URL}/orgs/${serverOrgId.value}/users`),
  enabled: computed(() => !!serverOrgId.value),
});
const serverCategories = computed<ActivityCategory[] | undefined>(() =>
  selectedCategory.value ? [selectedCategory.value] : undefined,
);
const serverSeverities = computed<ActivitySeverity[] | undefined>(() =>
  selectedSeverity.value ? [selectedSeverity.value] : undefined,
);
const serverActorUserId = computed(() => selectedActorUserId.value || undefined);

const { activities, activitiesLoading: isLoading } = useAllActivities({
  orgId: serverOrgId,
  categories: serverCategories,
  severities: serverSeverities,
  actorUserId: serverActorUserId,
  take: 200,
});

// Local narrowing over the loaded page — search + date range.
const filteredActivities = computed(() => {
  let rows = activities.value;

  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase();
    rows = rows.filter((a) => {
      const title = renderActivityTitle(a).toLowerCase();
      const org = a.orgName.toLowerCase();
      const actor = (a.actorDisplayName ?? "").toLowerCase();
      return title.includes(q) || org.includes(q) || actor.includes(q);
    });
  }

  if (dateFrom.value) {
    const fromMs = new Date(dateFrom.value).getTime();
    rows = rows.filter((a) => new Date(a.occurredOn).getTime() >= fromMs);
  }

  if (dateTo.value) {
    const toMs = new Date(dateTo.value).getTime();
    rows = rows.filter((a) => new Date(a.occurredOn).getTime() <= toMs);
  }

  return rows;
});

// Org dropdown — sourced from /api/orgs so the option list stays stable across filter
// changes (deriving from `activities` would collapse the list to only the selected org once
// a filter is applied). Falls back to whatever's in `activities` if the orgs request is
// still loading — gives a usable list immediately on first render.
const uniqueOrgs = computed(() => {
  if (allOrgsData.value && allOrgsData.value.length > 0) {
    return allOrgsData.value
      .map((o) => ({ id: o.orgId, name: o.orgName }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }
  const seen = new Map<string, string>();
  for (const a of activities.value) {
    if (!seen.has(a.orgId)) seen.set(a.orgId, a.orgName);
  }
  return Array.from(seen.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
});

// Actor dropdown — cascades from the Org filter when one is selected (uses /api/orgs/{orgId}/users
// for the full set of users in that org). With no org filter, falls back to deriving names from
// the loaded activities. This avoids showing a system-wide flat user list (potentially huge and
// full of cross-org combinations that return empty results).
const uniqueActors = computed(() => {
  if (serverOrgId.value && orgUsersData.value && orgUsersData.value.length > 0) {
    return orgUsersData.value
      .map((u) => {
        const fullName = `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim();
        const name = fullName.length > 0 ? fullName : (u.email ?? u.id);
        return { id: u.id, name };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }
  const seen = new Map<string, string>();
  for (const a of activities.value) {
    if (a.actorUserId && !seen.has(a.actorUserId)) {
      seen.set(a.actorUserId, a.actorDisplayName ?? a.actorUserId);
    }
  }
  return Array.from(seen.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
});

function clearFilters() {
  searchQuery.value = "";
  selectedOrgId.value = "";
  selectedCategory.value = "";
  selectedSeverity.value = "";
  selectedActorUserId.value = "";
  dateFrom.value = "";
  dateTo.value = "";
}

function formatDateTime(timestamp: string) {
  return new Date(timestamp).toLocaleString();
}

// =====================================================================
// DataTables column definitions
// =====================================================================
const tableOptions = {
  columns: [
    { data: "id", title: "ID", visible: false },
    {
      data: "occurredOn",
      title: "Date",
      render: (data: string) =>
        `<small class="text-muted">${formatDateTime(data)}</small>`,
    },
    {
      data: null,
      title: "Activity",
      render: (_data: unknown, _type: string, row: AllActivitiesRow) => {
        const icon = activityIconClass(row);
        const color = activityColorClass(row.severity);
        const title = renderActivityTitle(row);
        return `<div class="d-flex align-items-start gap-2">
          <i class="${icon} ${color} fs-5 mt-1"></i>
          <div>
            <div class="fw-medium">${title}</div>
            <small class="text-muted">${row.category} · ${row.eventType}</small>
          </div>
        </div>`;
      },
    },
    {
      data: "orgName",
      title: "Org",
      render: (data: string) =>
        `<span class="badge bg-dark">${data}</span>`,
    },
    {
      data: "actorDisplayName",
      title: "Actor",
      render: (data: string | null) =>
        data
          ? `<span class="text-muted">${data}</span>`
          : `<span class="text-muted fst-italic">System</span>`,
    },
    {
      data: "severity",
      title: "Severity",
      render: (data: ActivitySeverity) =>
        `<span class="badge ${activitySeverityBadgeClass(data)}">${data}</span>`,
    },
  ],
  pageLength: 50,
  layout: {
    topEnd: "",
    bottomEnd: "",
    bottomStart: "info",
  },
} as Config;
</script>

<template>
  <PanelGrid :columns="1">
    <PanelCard>
      <PageHeader
        title="Activity (All Orgs)"
        :breadcrumbs="[
          { label: 'Dashboard', href: '/' },
          { label: 'Admin' },
          { label: 'Activity' }
        ]"
      />

      <div class="card mb-4 mt-4">
        <div class="card-body">
          <h2 class="card-title">Filters</h2>
          <div class="row g-3">
            <div class="col-12 col-md-6 col-lg-3">
              <label class="form-label">Search</label>
              <input
                v-model="searchQuery"
                type="text"
                class="form-control"
                placeholder="Search activities…"
              />
            </div>
            <div class="col-12 col-md-6 col-lg-3">
              <label class="form-label">Org</label>
              <select v-model="selectedOrgId" class="form-select">
                <option value="">All Orgs</option>
                <option v-for="org in uniqueOrgs" :key="org.id" :value="org.id">
                  {{ org.name }}
                </option>
              </select>
            </div>
            <div class="col-12 col-md-6 col-lg-3">
              <label class="form-label">Category</label>
              <select v-model="selectedCategory" class="form-select">
                <option value="">All Categories</option>
                <option v-for="cat in ACTIVITY_CATEGORY_OPTIONS" :key="cat.value" :value="cat.value">
                  {{ cat.label }}
                </option>
              </select>
            </div>
            <div class="col-12 col-md-6 col-lg-3">
              <label class="form-label">Severity</label>
              <select v-model="selectedSeverity" class="form-select">
                <option value="">All Severities</option>
                <option v-for="sev in ACTIVITY_SEVERITY_OPTIONS" :key="sev.value" :value="sev.value">
                  {{ sev.label }}
                </option>
              </select>
            </div>
            <div class="col-12 col-md-6 col-lg-3">
              <label class="form-label">Actor</label>
              <select v-model="selectedActorUserId" class="form-select">
                <option value="">All Actors</option>
                <option v-for="actor in uniqueActors" :key="actor.id" :value="actor.id">
                  {{ actor.name }}
                </option>
              </select>
            </div>
            <div class="col-12 col-md-6 col-lg-3">
              <label class="form-label">From Date</label>
              <input v-model="dateFrom" type="date" class="form-control" />
            </div>
            <div class="col-12 col-md-6 col-lg-3">
              <label class="form-label">To Date</label>
              <input v-model="dateTo" type="date" class="form-control" />
            </div>
            <div class="col-12 col-md-6 col-lg-3 d-flex align-items-end">
              <button @click="clearFilters" class="bb-btn-secondary-cta w-100">
                <i class="bi bi-x-circle me-1"></i>Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="isLoading && activities.length === 0" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <DataView
        v-else
        :data="filteredActivities"
        :view-mode="'table'"
        :options="tableOptions"
      >
        <!-- Mobile card — mirrors the partner activity card: icon + title, a meta line
             with org/category and actor, severity badge and relative time. DataView
             switches to this automatically below the md breakpoint. -->
        <template #grid-card="{ row: activity }">
          <div class="card h-100 card-hover">
            <div class="card-body">
              <div class="d-flex align-items-start gap-3">
                <i
                  :class="[activityIconClass(activity), activityColorClass(activity.severity)]"
                  class="fs-5 mt-1 flex-shrink-0"
                ></i>
                <div class="flex-grow-1 min-w-0">
                  <div class="d-flex justify-content-between align-items-start gap-2 mb-1">
                    <h4 class="mb-0 fw-medium">{{ renderActivityTitle(activity) }}</h4>
                    <span class="badge flex-shrink-0" :class="activitySeverityBadgeClass(activity.severity)">
                      {{ activity.severity }}
                    </span>
                  </div>
                  <div class="d-flex flex-wrap align-items-center gap-2 small">
                    <span class="badge bg-dark">{{ activity.orgName }}</span>
                    <span class="text-muted">{{ activity.category }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="card-footer bg-transparent border-top-0 d-flex justify-content-between align-items-center gap-2">
              <small class="text-muted text-truncate">
                <i class="bi bi-person me-1"></i>{{ activity.actorDisplayName || "System" }}
              </small>
              <small class="text-muted flex-shrink-0">
                <i class="bi bi-clock me-1"></i>{{ formatActivityTimestamp(activity.occurredOn) }}
              </small>
            </div>
          </div>
        </template>

        <template #empty-state>
          <div class="text-center py-5">
            <i class="bi bi-activity display-1 text-muted"></i>
            <h3 class="mt-3 text-muted">No activity found</h3>
            <p class="text-muted">Try adjusting your filters to see results.</p>
          </div>
        </template>
      </DataView>
    </PanelCard>
  </PanelGrid>
</template>
